import { getHydratedEntityId } from 'features/cart/utils/hydration';

import { IBackendCartEntries } from '@rbi-ctg/menu';
import { ILocalizedMenuEntry, IndexedMenu } from 'state/unified-menu/types';
import { CartEntryType } from 'utils/cart/types';

import { IMenuObjectDisplayData } from './get-menu-object-name-map';

/**
 * OneMenu equivalent of getMenuObjectNameMap: cart/order entry name is baked in at
 * add-to-cart or order-placement time and never re-resolved after a later app language
 * change. Unlike the legacy path, OneMenu has no separate "menuObject" tree to walk — the
 * fresh data lives in `indexedMenu`, keyed by ids composed differently per level: an item is
 * looked up by its own sanityId, a modifier GROUP (CartEntryType.itemOption) by
 * `${itemId}$${groupId}` (getHydratedEntityId), and a MODIFIER/multiplier
 * (CartEntryType.itemOptionModifier — the level that actually carries the composed "Regular
 * BBQ Dip Cup"-style display text) by its own sanityId as-is, matching
 * use-reprice-cart-entries.ts's equivalent multiplier lookup. Walk the actual order
 * cart-entry tree (there's no separate menu-object tree here) so every level can be
 * re-localized by its own sanityId.
 */
export const getOneMenuDisplayDataMap = (
  cartEntries: IBackendCartEntries[] | undefined,
  indexedMenu: IndexedMenu<ILocalizedMenuEntry>
): Map<string, IMenuObjectDisplayData> => {
  const displayDataMap = new Map<string, IMenuObjectDisplayData>();

  const setDisplayData = (sanityId: string, menuEntry: ILocalizedMenuEntry | undefined) => {
    const name = menuEntry?.name?.locale;
    if (name) {
      displayDataMap.set(sanityId, { name });
    }
  };

  const visit = (entries: IBackendCartEntries[] | undefined, parentItemId: string | undefined) => {
    (entries || []).forEach(entry => {
      if (entry.type === CartEntryType.item) {
        setDisplayData(entry.sanityId, indexedMenu.find(entry.sanityId));
        visit(entry.children, entry.sanityId);
        return;
      }

      if (entry.type === CartEntryType.itemOptionModifier) {
        // The multiplier level (e.g. "Regular BBQ Dip Cup") — looked up by its own sanityId
        // as-is (already hydrated/composite), since this is what cart-entry-body.tsx /
        // OrderItem's description actually display, not the modifier group's own name.
        setDisplayData(entry.sanityId, indexedMenu.find(entry.sanityId));
      } else if (parentItemId) {
        // A modifier group (CartEntryType.itemOption) or combo slot/other intermediate level —
        // parent-prefixed lookup, matching use-reprice-cart-entries.ts's modifierId scheme.
        setDisplayData(
          entry.sanityId,
          indexedMenu.find(getHydratedEntityId(entry.sanityId, parentItemId))
        );
      } else {
        setDisplayData(entry.sanityId, indexedMenu.find(entry.sanityId));
      }

      visit(entry.children, parentItemId);
    });
  };

  visit(cartEntries, undefined);

  return displayDataMap;
};
